// League Context - manages league state across the app
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  getLeaguesForUser,
  getLeague,
  createLeague as createLeagueService,
  addMemberToLeague,
  removeMemberFromLeague,
  updateLeague as updateLeagueService,
  createLeagueInvitation,
  getLeagueInvitation,
  acceptLeagueInvitation
} from '../services/leagueService';
import { getCompetitionsForLeague } from '../services/competitionService';
import { getGameSectionsForLeague } from '../services/gameSectionService';

const LeagueContext = createContext();

export const useLeague = () => {
  const context = useContext(LeagueContext);
  if (!context) {
    throw new Error('useLeague must be used within a LeagueProvider');
  }
  return context;
};

export const LeagueProvider = ({ children }) => {
  const { user, authUid } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [currentLeague, setCurrentLeague] = useState(null);
  const [leagueCompetitions, setLeagueCompetitions] = useState([]);
  const [leagueGameSections, setLeagueGameSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user ID - prefer uid from auth, fallback to custom player IDs
  const getUserId = useCallback(() => {
    return user?.uid || authUid || user?.odplayerId || user?.oduserId;
  }, [user, authUid]);

  // Load user's leagues
  const loadUserLeagues = useCallback(async () => {
    const userId = getUserId();

    if (!userId) {
      setLeagues([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userLeagues = await getLeaguesForUser(userId);
      setLeagues(userLeagues);
      setError(null);
    } catch (err) {
      console.error('Error loading leagues:', err);
      setError('Failed to load leagues');
    } finally {
      setLoading(false);
    }
  }, [getUserId, user, authUid]);

  // Load a specific league and its data
  const loadLeague = useCallback(async (leagueId) => {
    try {
      setLoading(true);
      const league = await getLeague(leagueId);
      if (league) {
        setCurrentLeague(league);

        // Load league competitions and game sections
        const [competitions, gameSections] = await Promise.all([
          getCompetitionsForLeague(leagueId),
          getGameSectionsForLeague(leagueId)
        ]);

        setLeagueCompetitions(competitions);
        setLeagueGameSections(gameSections);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading league:', err);
      setError('Failed to load league');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new league
  const createLeague = async (name, description) => {
    const userId = getUserId();
    if (!userId) throw new Error('Must be logged in to create a league');

    const userName = user?.displayName || 'Unknown';

    const newLeague = await createLeagueService(name, description, userId, userName);
    setLeagues(prev => [...prev, newLeague]);
    return newLeague;
  };

  // Update league details
  const updateLeague = async (leagueId, updates) => {
    await updateLeagueService(leagueId, updates);
    setLeagues(prev => prev.map(l => l.id === leagueId ? { ...l, ...updates } : l));
    if (currentLeague?.id === leagueId) {
      setCurrentLeague(prev => ({ ...prev, ...updates }));
    }
  };

  // Add member to current league
  const addMember = async (playerId, displayName) => {
    if (!currentLeague) throw new Error('No league selected');
    await addMemberToLeague(currentLeague.id, playerId, displayName);
    await loadLeague(currentLeague.id);
  };

  // Remove member from current league
  const removeMember = async (playerId) => {
    if (!currentLeague) throw new Error('No league selected');
    await removeMemberFromLeague(currentLeague.id, playerId);
    await loadLeague(currentLeague.id);
  };

  // Generate invite link for league
  const generateInviteLink = async () => {
    if (!currentLeague || !user) throw new Error('No league selected');

    const userName = user.displayName || 'Unknown';
    const invitation = await createLeagueInvitation(
      currentLeague.id,
      currentLeague.name,
      userName
    );

    // Return the invitation URL
    return `${window.location.origin}/join-league/${invitation.id}`;
  };

  // Accept a league invitation
  const acceptInvite = async (inviteId) => {
    const userId = getUserId();
    if (!userId) throw new Error('Must be logged in to accept invitation');

    const userName = user?.displayName || 'Unknown';

    const leagueId = await acceptLeagueInvitation(inviteId, userId, userName);
    await loadUserLeagues();
    return leagueId;
  };

  // Get invitation details
  const getInvitation = async (inviteId) => {
    return await getLeagueInvitation(inviteId);
  };

  // Check if current user is admin of current league
  const isLeagueAdmin = currentLeague && getUserId() &&
    currentLeague.adminId === getUserId();

  // Refresh league data
  const refreshLeague = async () => {
    if (currentLeague) {
      await loadLeague(currentLeague.id);
    }
  };

  // Refresh game sections
  const refreshGameSections = async () => {
    if (currentLeague) {
      const gameSections = await getGameSectionsForLeague(currentLeague.id);
      setLeagueGameSections(gameSections);
    }
  };

  // Load user leagues on mount
  useEffect(() => {
    loadUserLeagues();
  }, [loadUserLeagues]);

  const value = {
    // State
    leagues,
    currentLeague,
    leagueCompetitions,
    leagueGameSections,
    loading,
    error,
    isLeagueAdmin,

    // Actions
    loadUserLeagues,
    loadLeague,
    createLeague,
    updateLeague,
    addMember,
    removeMember,
    generateInviteLink,
    acceptInvite,
    getInvitation,
    refreshLeague,
    refreshGameSections,
    setCurrentLeague
  };

  return (
    <LeagueContext.Provider value={value}>
      {children}
    </LeagueContext.Provider>
  );
};

export default LeagueContext;
